/**
 * useAnalytics — React hook for auto-initializing analytics
 *
 * Phase 17.2.1 + PostHog (#168):
 * - Listens to auth state, auto-calls initAnalytics/stopAnalytics.
 * - Initializes PostHog SDK on mount.
 * - Identifies/resets PostHog user on auth changes.
 * - Tracks page views on route changes.
 *
 * Usage in App root:
 *   useAnalytics();  // auto-initializes analytics when user logs in
 *
 * Usage in components:
 *   import { trackEvent } from '@/core/analytics';
 *   trackEvent('novel_created', { genre: 'fantasy' });
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useAuthStore } from '@/shared/stores/authStore';
import { initAnalytics, stopAnalytics, trackEvent } from './index';
import { initPostHog, identifyUser, resetUser, capturePageView } from './posthog';

/**
 * Hook that auto-manages analytics lifecycle based on auth state.
 * Call once in the app root (e.g., App.tsx).
 */
export function useAnalytics(): void {
  const user = useAuthStore((s) => s.user);
  const prevUserId = useRef<string | null>(null);

  // Initialize PostHog on first mount
  useEffect(() => {
    initPostHog();
  }, []);

  // Auth state → Supabase analytics + PostHog identify
  useEffect(() => {
    const currentId = user?.id ?? null;

    if (currentId && currentId !== prevUserId.current) {
      // User logged in or changed
      initAnalytics(currentId);
      identifyUser(currentId, {
        email: user?.email,
      });
      trackEvent('user_logged_in', { method: 'email' });
    } else if (!currentId && prevUserId.current) {
      // User logged out
      stopAnalytics();
      resetUser();
    }

    prevUserId.current = currentId;

    return () => {
      // Cleanup on unmount (or StrictMode simulated unmount)
      stopAnalytics();
      resetUser();
      // Reset prevUserId so StrictMode second mount re-triggers initAnalytics
      prevUserId.current = null;
    };
  }, [user?.id, user?.email]);
}

/**
 * Hook that tracks page views on route changes.
 * Uses React Router's useLocation to detect navigation.
 * Call once in the app root alongside useAnalytics().
 */
export function usePageViewTracker(): void {
  const location = useLocation();
  const prevPath = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname;
    if (path !== prevPath.current) {
      capturePageView(path);
      prevPath.current = path;
    }
  }, [location.pathname]);
}